import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Brain, ChevronDown, ChevronUp, X, ZoomIn, Target } from 'lucide-react';
import CollapsibleTable from './CollapsibleTable';

// Type definitions based on the provided JSON structure
interface CreativeReviewData {
  json_output: {
    overall_campaign_score: number;
    campaign_objective: string;
    total_creatives: number;

    executive_summary: Record<
      string,
      {
        image_idx: number;
        image_display_key: string;
        overall_performance: number;
        performance_status: 'Green' | 'Yellow' | 'Red';
        action_required: 'Proceed' | 'Optimize' | 'Revise';
        message_clarity: number;
        trust_level: number;
        verdict: 'PROCEED AS IS' | 'OPTIMIZE FIRST' | 'NEEDS MAJOR WORK';
        bottom_line: string;
      }
    >;

    recommendations: Record<
      string,
      {
        image_idx: number;
        top_fixes: Array<{
          fix: string;
          impact: string;
          effort_level: number;
          effort_description: 'Easy' | 'Medium' | 'Hard';
          lift: number;
        }>;
        total_projected_lift: number;
        current_success_rate: number;
        projected_success_rate: number;
      }
    >;

    persona_insights: Record<
      string,
      {
        image_idx: number;
        loves_it: {
          percentage: number;
          profile: string;
          driver: string;
        };
        on_fence: {
          percentage: number;
          profile: string;
          barrier: string;
        };
        not_interested: {
          percentage: number;
          profile: string;
          issue: string;
        };
      }
    >;

    creative_analyses: Record<string, any>; // Detailed analysis data
  };
  markdown_output?: string;
  images?: string[];
}

interface CreativeReviewNewProps {
  data: CreativeReviewData;
  isExporting?: boolean;
  className?: string;
}

const CreativeReviewNew: React.FC<CreativeReviewNewProps> = ({
  data,
  isExporting = false,
  className = '',
}) => {
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});
  const [selectedImage, setSelectedImage] = useState<{ url: string; alt: string } | null>(null);

  // Auto-expand all sections when exporting
  React.useEffect(() => {
    if (isExporting) {
      setExpandedSections({
        'detailed-analysis': true,
        'performance-metrics': true,
        insights: true,
        recommendations: true,
      });
    }
  }, [isExporting]);

  if (!data?.json_output) {
    return (
      <div className="text-center py-12 text-text-secondary">
        <Target className="w-16 h-16 mx-auto mb-4 text-gray-300" />
        <p className="text-lg">No creative review data available</p>
        <div className="mt-4 p-4 bg-gray-100 rounded text-left text-xs">
          <p className="font-medium mb-2">Debug Info:</p>
          <pre>
            {JSON.stringify(
              {
                hasData: !!data,
                dataKeys: data ? Object.keys(data) : 'No data',
                dataStructure: data
                  ? JSON.stringify(data, null, 2).substring(0, 500) + '...'
                  : 'No data',
              },
              null,
              2,
            )}
          </pre>
        </div>
      </div>
    );
  }

  const { json_output } = data;
  const creatives = Object.keys(json_output.executive_summary || {});

  const toggleSection = (sectionKey: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionKey]: !prev[sectionKey],
    }));
  };

  const openImageModal = (url: string, alt: string) => {
    setSelectedImage({ url, alt });
  };

  const closeImageModal = () => {
    setSelectedImage(null);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Green':
        return 'text-green-600 bg-green-50 border-green-200';
      case 'Yellow':
        return 'text-amber-600 bg-amber-50 border-amber-200';
      case 'Red':
        return 'text-red-600 bg-red-50 border-red-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getVerdictStyles = (verdict: string) => {
    switch (verdict) {
      case 'PROCEED AS IS':
        return 'text-green-700 bg-green-100 border-green-300';
      case 'OPTIMIZE FIRST':
        return 'text-amber-700 bg-amber-100 border-amber-300';
      case 'NEEDS MAJOR WORK':
        return 'text-red-700 bg-red-100 border-red-300';
      default:
        return 'text-gray-700 bg-gray-100 border-gray-300';
    }
  };

  const getEffortBadgeStyles = (effort: string) => {
    switch (effort) {
      case 'Easy':
        return 'text-green-700 bg-green-100';
      case 'Medium':
        return 'text-amber-700 bg-amber-100';
      case 'Hard':
        return 'text-red-700 bg-red-100';
      default:
        return 'text-gray-700 bg-gray-100';
    }
  };

  const getImageForCreative = (imageIdx: number): string | null => {
    if (!data.images || !data.images[imageIdx]) {
      return null;
    }

    const imageData = data.images[imageIdx];

    // Check if it's already a data URL
    if (imageData.startsWith('data:image/')) {
      return imageData;
    }

    // Otherwise, assume it's base64 and add the data URL prefix
    return `data:image/jpeg;base64,${imageData}`;
  };

  const getStatusEmoji = (status: string) => {
    switch (status) {
      case 'Green':
        return '🟢';
      case 'Yellow':
        return '🟡';
      case 'Red':
        return '🔴';
      default:
        return '⚪';
    }
  };

  const renderPersonaReactionsTable = (creativeKey: string) => {
    const analysis = json_output.creative_analyses?.[creativeKey];
    if (!analysis?.persona_reactions || analysis.persona_reactions.length === 0) {
      return null;
    }

    // Prepare table data for CollapsibleTable
    const headers = [
      'Persona',
      'Composite Score',
      'First Notice',
      'Stop/Scroll',
      'Clear/Confused',
      'Trust Level',
      'Biggest Barrier',
      'Key Change Needed',
    ];

    const tableData = {
      type: 'table' as const,
      title: 'Individual Persona Responses',
      headers,
      data: analysis.persona_reactions.map((persona: any) => ({
        Persona: persona.persona_name || 'Unknown',
        'Composite Score': persona.composite_score
          ? `${Math.round(persona.composite_score)}/10`
          : 'N/A',
        'First Notice': persona.first_notice || 'N/A',
        'Stop/Scroll': persona.stop_or_scroll || 'N/A',
        'Clear/Confused': persona.clear_or_confused || 'N/A',
        'Trust Level': persona.trust_level ? `${persona.trust_level}/10` : 'N/A',
        'Biggest Barrier': persona.biggest_barrier || 'N/A',
        'Key Change Needed': persona.one_key_change || 'N/A',
      })),
    };

    return (
      <div className="mb-8">
        <h4 className="text-md font-medium text-gray-900 mb-4">Individual Persona Responses</h4>
        <CollapsibleTable
          table={tableData}
          maxInitialRows={5}
          showExportButtons={true}
          showFullContent={false}
          className="mb-4"
          isExporting={isExporting}
        />
      </div>
    );
  };

  const renderMainTable = () => {
    // Check if we have any creatives to display
    if (!creatives || creatives.length === 0) {
      return (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h1 className="text-xl font-semibold text-gray-900 mb-2">Creative Review Analysis</h1>
          <p className="text-gray-600 text-sm mb-4">No creatives found in the executive summary</p>
          <div className="text-xs bg-gray-100 p-4 rounded">
            <p className="font-medium mb-2">Available data keys:</p>
            <pre>{JSON.stringify(Object.keys(json_output), null, 2)}</pre>
          </div>
        </div>
      );
    }

    // Build headers
    const headers = [
      '#',
      'Creative',
      'Overall Score',
      'Performance Status',
      'Trust Level',
      'Verdict',
      'Action Required',
    ];

    // Create table data with proper image objects for CollapsibleTable
    const tableData = {
      type: 'table' as const,
      title: 'Performance Overview',
      headers,
      data: creatives.map((creativeKey, index) => {
        const creative = json_output.executive_summary[creativeKey];
        const imageUrl = getImageForCreative(creative?.image_idx);

        return {
          '#': (index + 1).toString(),
          Creative: imageUrl
            ? {
                type: 'image' as const,
                src: imageUrl,
                alt: `Creative ${creative?.image_idx + 1 || index + 1}`,
                onImageClick: () =>
                  openImageModal(imageUrl, `Creative ${creative?.image_idx + 1 || index + 1}`),
              }
            : `Creative ${creative?.image_idx + 1 || index + 1}`,
          'Overall Score': `${creative?.overall_performance || 0}/10`,
          'Performance Status': `${getStatusEmoji(creative?.performance_status || 'Unknown')} ${creative?.performance_status || 'Unknown'}`,
          'Trust Level': `${creative?.trust_level || 0}/10`,
          Verdict: creative?.verdict || 'Unknown',
          'Action Required': creative?.action_required || 'Review',
        };
      }),
    };

    return (
      <div className="space-y-6">
        {/* Header - Following survey pattern */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h1 className="text-xl font-semibold text-gray-900 mb-2">Creative Review Analysis</h1>
          <p className="text-gray-600 text-sm">
            Campaign: {json_output.campaign_objective} • Overall Score:{' '}
            {json_output.overall_campaign_score}/10 •{json_output.total_creatives} creative
            {json_output.total_creatives > 1 ? 's' : ''} analyzed
          </p>
        </div>

        {/* Main Results Table - Using CollapsibleTable directly like survey components */}
        <CollapsibleTable
          table={tableData}
          questionType="table"
          maxInitialRows={10}
          showFullContent={true}
          showExportButtons={!isExporting}
          isExporting={isExporting}
        />
      </div>
    );
  };

  const renderExecutiveSummaryReport = () => {
    return (
      <div className="space-y-8">
        {/* Executive Creative Intelligence Report - Hidden */}
        <div>
          {/* Individual Creative Analysis - SHOW ALL CREATIVES */}
          {creatives.map((creativeKey, _creativeIndex) => {
            const creative = json_output.executive_summary[creativeKey];
            const recommendations = json_output.recommendations[creativeKey];
            const insights = json_output.persona_insights[creativeKey];
            const imageUrl = getImageForCreative(creative?.image_idx);
            // Handle backward compatibility: historical reports use "wont_convert", new reports use "not_interested"
            const notInterested = insights?.not_interested || (insights as any)?.wont_convert;

            return (
              <div
                key={creativeKey}
                className="mb-12 pb-8 border-b border-gray-200 last:border-b-0"
              >
                {/* Creative Header with Image */}
                <div className="flex items-start justify-between mb-6">
                  <div className="flex items-start space-x-6">
                    {/* Image */}
                    <div className="flex-shrink-0">
                      {imageUrl ? (
                        <div
                          className="relative cursor-pointer group w-32 h-24"
                          onClick={() =>
                            openImageModal(imageUrl, `Creative ${creative?.image_idx + 1}`)
                          }
                        >
                          <img
                            src={imageUrl}
                            alt={`Creative ${creative?.image_idx + 1}`}
                            className="w-full h-full object-cover rounded-lg border border-gray-200 group-hover:opacity-90 transition-opacity"
                          />
                          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                            <ZoomIn className="w-6 h-6 text-white bg-black bg-opacity-50 rounded p-1" />
                          </div>
                        </div>
                      ) : (
                        <div className="w-32 h-24 bg-light-gray rounded-lg border border-gray-200 flex items-center justify-center">
                          <Target className="w-8 h-8 text-gray-400" />
                        </div>
                      )}
                    </div>

                    {/* Creative Info */}
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">
                        Creative {creative?.image_idx + 1}
                      </h3>
                      <div className="flex items-center space-x-4 mb-3">
                        <div className="text-2xl font-bold text-primary">
                          {creative?.overall_performance || 0}/10
                        </div>
                        <div
                          className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(creative?.performance_status || 'Unknown')}`}
                        >
                          {creative?.performance_status || 'Unknown'}
                        </div>
                        <div
                          className={`px-3 py-2 rounded-lg text-xs font-medium border ${getVerdictStyles(creative?.verdict || 'NEEDS REVIEW')}`}
                        >
                          {creative?.verdict || 'NEEDS REVIEW'}
                        </div>
                      </div>
                      <p className="text-sm text-gray-600 mb-2">
                        <strong>Bottom Line:</strong> {creative?.bottom_line || 'Analysis pending'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* 30-Second Summary Table for This Creative */}
                <div className="mb-8">
                  <h4 className="text-md font-medium text-gray-900 mb-4">Performance Metrics</h4>
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse bg-white rounded-lg border border-gray-200">
                      <thead>
                        <tr className="border-b border-gray-200 bg-gray-50">
                          <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">
                            Metric
                          </th>
                          <th className="text-center py-3 px-4 text-sm font-medium text-gray-700">
                            Score
                          </th>
                          <th className="text-center py-3 px-4 text-sm font-medium text-gray-700">
                            Status
                          </th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">
                            Action Required
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="border-b border-gray-100">
                          <td className="py-3 px-4 text-sm font-medium text-gray-900">
                            Overall Performance
                          </td>
                          <td className="py-3 px-4 text-sm text-center font-semibold">
                            {creative?.overall_performance || 0}/10
                          </td>
                          <td className="py-3 px-4 text-sm text-center">
                            {getStatusEmoji(creative?.performance_status)}
                          </td>
                          <td className="py-3 px-4 text-sm text-gray-600">
                            {creative?.action_required || 'Review'}
                          </td>
                        </tr>
                        <tr className="border-b border-gray-100">
                          <td className="py-3 px-4 text-sm font-medium text-gray-900">
                            Message Clarity
                          </td>
                          <td className="py-3 px-4 text-sm text-center font-semibold">
                            {creative?.message_clarity || 0}/10
                          </td>
                          <td className="py-3 px-4 text-sm text-center text-gray-600">
                            {(creative?.message_clarity || 0) >= 7 ? 'Clear' : 'Confused'}
                          </td>
                          <td className="py-3 px-4 text-sm text-gray-600">
                            {(creative?.message_clarity || 0) >= 7 ? 'None' : 'Simplify'}
                          </td>
                        </tr>
                        <tr className="border-b border-gray-100">
                          <td className="py-3 px-4 text-sm font-medium text-gray-900">
                            Trust Level
                          </td>
                          <td className="py-3 px-4 text-sm text-center font-semibold">
                            {creative?.trust_level || 0}/10
                          </td>
                          <td className="py-3 px-4 text-sm text-center text-gray-600">
                            {(creative?.trust_level || 0) >= 7
                              ? 'High'
                              : (creative?.trust_level || 0) >= 5
                                ? 'Medium'
                                : 'Low'}
                          </td>
                          <td className="py-3 px-4 text-sm text-gray-600">
                            {(creative?.trust_level || 0) >= 7 ? 'None' : 'Add proof'}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Top 3 Fixes for This Creative */}
                {recommendations &&
                  recommendations.top_fixes &&
                  recommendations.top_fixes.length > 0 && (
                    <div className="mb-8">
                      <h4 className="text-md font-medium text-gray-900 mb-4">
                        Top 3 Fixes (Do These First)
                      </h4>

                      <div className="overflow-x-auto">
                        <table className="w-full border-collapse bg-white rounded-lg border border-gray-200">
                          <thead>
                            <tr className="border-b border-gray-200 bg-gray-50">
                              <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">
                                Priority
                              </th>
                              <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">
                                Fix
                              </th>
                              <th className="text-center py-3 px-4 text-sm font-medium text-gray-700">
                                Effort
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {recommendations.top_fixes.slice(0, 3).map((fix, index) => (
                              <tr key={index} className="border-b border-gray-100">
                                <td className="py-3 px-4 text-sm font-medium text-gray-900">
                                  #{index + 1}
                                </td>
                                <td className="py-3 px-4 text-sm text-gray-900">{fix.fix}</td>
                                <td className="py-3 px-4 text-sm text-center">
                                  <span
                                    className={`px-2 py-1 rounded text-xs font-medium ${getEffortBadgeStyles(fix.effort_description)}`}
                                  >
                                    {fix.effort_description}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      {/* Total Projected Lift section - Hidden */}
                    </div>
                  )}

                {/* Audience Insights for This Creative */}
                {insights && (
                  <div>
                    <h4 className="text-md font-medium text-gray-900 mb-4">Audience Insights</h4>

                    <div className="space-y-4">
                      {/* Who Loves It */}
                      <div className="border-l-4 border-[#07E5D1] pl-4">
                        <div className="flex items-baseline justify-between mb-1">
                          <h5 className="font-medium text-gray-900">Who Loves It</h5>
                          <span className="text-sm text-gray-600">
                            ({insights.loves_it.percentage}% of audience)
                          </span>
                        </div>
                        <p className="text-sm text-gray-700 mb-1">
                          • Profile: {insights.loves_it.profile}
                        </p>
                        <p className="text-sm text-gray-700 mb-1">
                          • Driver: <em>"{insights.loves_it.driver}"</em>
                        </p>
                        <p className="text-sm font-medium text-gray-900">
                          • Action: Double down on this segment
                        </p>
                      </div>

                      {/* Who's On The Fence */}
                      <div className="border-l-4 border-gray-300 pl-4">
                        <div className="flex items-baseline justify-between mb-1">
                          <h5 className="font-medium text-gray-900">Who's On The Fence</h5>
                          <span className="text-sm text-gray-600">
                            ({insights.on_fence.percentage}% of audience)
                          </span>
                        </div>
                        <p className="text-sm text-gray-700 mb-1">
                          • Profile: {insights.on_fence.profile}
                        </p>
                        <p className="text-sm text-gray-700 mb-1">
                          • Barrier: <em>"{insights.on_fence.barrier}"</em>
                        </p>
                        <p className="text-sm font-medium text-gray-900">
                          • Action: Address their specific concerns
                        </p>
                      </div>

                      {/* Who's Not Interested */}
                      <div className="border-l-4 border-gray-200 pl-4">
                        <div className="flex items-baseline justify-between mb-1">
                          <h5 className="font-medium text-gray-900">Who's Not Interested</h5>
                          <span className="text-sm text-gray-600">
                            ({notInterested?.percentage}% of audience)
                          </span>
                        </div>
                        <p className="text-sm text-gray-700 mb-1">
                          • Profile: {notInterested?.profile}
                        </p>
                        <p className="text-sm text-gray-700 mb-1">
                          • Issue: <em>"{notInterested?.issue}"</em>
                        </p>
                        <p className="text-sm font-medium text-gray-900">
                          • Action: Accept loss or pivot strategy
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Individual Persona Responses Table */}
                {renderPersonaReactionsTable(creativeKey)}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className={`space-y-8 ${className}`}>
      {/* Main Results Table - Always visible like AB test */}
      {renderMainTable()}

      {/* Detailed Analysis Dropdown - Using exact simulation results pattern */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <button
          onClick={() => toggleSection('detailed-analysis')}
          className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center space-x-3">
            <Brain className="h-5 w-5 text-gray-600" />
            <span className="font-medium text-gray-900">Detailed Analysis</span>
          </div>
          {expandedSections['detailed-analysis'] ? (
            <ChevronUp className="h-5 w-5 text-gray-400" />
          ) : (
            <ChevronDown className="h-5 w-5 text-gray-400" />
          )}
        </button>

        {expandedSections['detailed-analysis'] && (
          <div className="border-t border-gray-100 p-6 bg-white">
            {/* Executive Summary Report with all detailed analysis */}
            {renderExecutiveSummaryReport()}
          </div>
        )}
      </div>

      {/* Image Modal */}
      {selectedImage &&
        createPortal(
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
            <div className="relative bg-white rounded-lg shadow-2xl border border-gray-200 overflow-hidden max-w-4xl max-h-[90vh] w-full flex flex-col">
              <button
                onClick={closeImageModal}
                className="absolute top-4 right-4 z-10 bg-white hover:bg-gray-100 text-gray-700 hover:text-gray-900 rounded-full p-2 shadow-lg transition-colors border border-gray-200"
              >
                <X className="h-5 w-5" />
              </button>

              <div className="flex-1 flex items-center justify-center p-4 pt-16 pb-16">
                <img
                  src={selectedImage.url}
                  alt={selectedImage.alt}
                  className="max-w-full max-h-full object-contain"
                />
              </div>

              <div className="bg-gray-50 border-t border-gray-200 px-6 py-4">
                <div className="text-center">
                  <h3 className="text-base font-medium text-gray-900">{selectedImage.alt}</h3>
                  <p className="text-xs text-gray-500 mt-1">Press ESC or click outside to close</p>
                </div>
              </div>
            </div>

            <div className="absolute inset-0 -z-10" onClick={closeImageModal} />
          </div>,
          document.body,
        )}
    </div>
  );
};

export default CreativeReviewNew;
